import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography,
} from '@mui/material';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { requestError } from './messages';
import type { ApiData, SharePointImportAll } from './types';

const API = 'api/integrations/sharepoint';

interface ImportAllControlsProps {
  workspaceId: number;
  importAll: SharePointImportAll;
  onUpdated: () => Promise<void>;
}

function count(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// "Import all": a new client's lease files aren't in Strada yet, so once Strada turns it on every file waiting for
// review can be queued at once instead of pressing Process on each.
export default function ImportAllControls({ workspaceId, importAll, onUpdated }: ImportAllControlsProps): JSX.Element | null {
  const { enqueueSnackbar } = useSnackbar();
  const [confirming, setConfirming] = useState(false);
  const { mutate: queueAll, isLoading: queueing } = useMutation(
    async () => axios({ url: `${API}/import-all/`, method: 'post', params: { workspace: workspaceId } }),
    {
      onSuccess: async (res: AxiosResponse<ApiData<{ queued: number }>>): Promise<void> => {
        setConfirming(false);
        enqueueSnackbar(`Queued ${count(res.data.data.queued, 'file')}. Each tenant's lease is imported first, then its amendments.`);
        await onUpdated();
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'import all files'), { variant: 'error' }); },
    },
  );

  const {
    allowed, waiting, queued, buildings_without_property: missing, daily_lease_limit: dailyLimit,
  } = importAll;
  if (queued === 0 && (waiting === 0 || (!allowed && waiting < 2))) {
    return null;
  }
  return (
    <Box sx={{ mb: 2 }}>
      {queued > 0 && (
        <Typography color='text.secondary' sx={{ mb: 1 }}>
          {allowed
            ? `Importing ${count(queued, 'more file')} in the background: each tenant's lease first, then its amendments once the lease has been read.`
            : `Import all is paused with ${count(queued, 'file')} still queued. Contact Strada to resume it.`}
        </Typography>
      )}
      {allowed && waiting > 0 && (
        <>
          <Button variant='contained' size='small' disabled={missing.length > 0 || queueing} onClick={(): void => { setConfirming(true); }}>
            {`Import all (${waiting})`}
          </Button>
          {missing.length > 0 && (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
              {`First confirm a property for ${count(missing.length, 'building folder')} below: ${missing.join(', ')}`}
            </Typography>
          )}
        </>
      )}
      {!allowed && waiting > 1 && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
          {`To import all ${waiting} files at once, ask Strada to turn on Import all for this workspace.`}
        </Typography>
      )}
      <Dialog open={confirming} onClose={(): void => { setConfirming(false); }}>
        <DialogTitle>{`Import ${count(waiting, 'file')}?`}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Strada imports each tenant folder&apos;s lease first, then the folder&apos;s amendments once the lease has been read, so they attach to it. This runs in the background and can take several hours.
          </DialogContentText>
          <DialogContentText>
            {`Strada processes up to ${dailyLimit} leases a day for this workspace. Files past that wait for Strada's approval.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(): void => { setConfirming(false); }}>Cancel</Button>
          <Button variant='contained' disabled={queueing} onClick={(): void => { queueAll(); }}>Import all</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
